import React from "react"
import { useOrder } from "./OrderContext"
import { supabase } from "@/lib/supabase"

import ReactHowler from 'react-howler'
import { useParams } from "react-router-dom"
import { cacheFile, getCachedFile } from "@/pages/order/PDF"
import { SparklesIcon } from "lucide-react"

const AudioContext = React.createContext()

const defaultSettings = {
    timeJump: 5,
    shortcuts: {
        'assistant': {
            key: 'cltr+k',
            labelKey: 'cltr+k',
            label: 'Assistant',
        },
        'play/pause': {
            key: 'escape',
            labelKey: 'escape',
            label: 'Lecture / Pause',
        },
        'skip-backwards': {
            key: 'ctrl+&',
            labelKey: 'ctrl+1',
            label: 'Reculer',
        },
        'skip-forward': {
            key: 'ctrl+é',
            labelKey: 'ctrl+2',
            label: 'Avancer',
        },
        'speed-down': {
            key: 'ctrl+"',
            labelKey: "ctrl+3",
            label: 'Ralentir',
        },
        'speed-up': {
            key: "ctrl+'",
            labelKey: 'ctrl+4',
            label: 'Accélérer',
        },
        'rewind-start': {
            key: 'ctrl+à',
            labelKey: 'ctrl+0',
            label: 'Revenir au début',
        },
    }
};

export function AudioProvider({ children }) {
    const { order } = useOrder()

    const [uri, setUri] = React.useState(null)
    const [audioSettings, setAudioSettings] = React.useState(() => {
        const savedAudioSettings = localStorage.getItem('audioSettings');
        return savedAudioSettings ? JSON.parse(savedAudioSettings) : defaultSettings;
    });

    const [seek, setSeek] = React.useState(0)
    const [position, setPosition] = React.useState(0)
    const [loading, setLoading] = React.useState(true)
    const [format, setFormat] = React.useState(null)

    const [playing, setPlaying] = React.useState(false);
    const [rate, setRate] = React.useState(1);

    const playerNotification = React.useRef(null);
    const player = React.useRef(null);
    const playingRef = React.useRef(false);

    React.useEffect(() => { playingRef.current = playing }, [playing])
    React.useEffect(() => { localStorage.setItem('audioSettings', JSON.stringify(audioSettings)) }, [audioSettings])

    React.useEffect(() => {
        const fetchUri = async (path) => {
            const fileExtension = path?.split('.').pop()?.toLowerCase();
            setFormat(fileExtension)

            const cachedBlob = await getCachedFile(path);
            if (cachedBlob) {
                console.log("📂 Chargement de l'audio depuis le cache");
                setUri(URL.createObjectURL(cachedBlob.blob));
                return;
            }


            const { data: { signedUrl } } = await supabase.storage.from("folders").createSignedUrl(
                path,
                60
            )


            const response = await fetch(signedUrl);
            const blob = await response.blob();

            const mimeType = `audio/${fileExtension || 'mp3'}`; // Dynamically set the MIME type based on the extension
            const audioBlob = new Blob([blob], { type: mimeType });

            // Stocker en cache
            await cacheFile(path, audioBlob, order.pydio_id);
            setUri(URL.createObjectURL(audioBlob));
        };

        if (order?.audio?.file) {
            fetchUri(`${order.company.id}/${order.folder_id}/${order.audio.file.name}`)
        }
    }, [order?.audio?.file])

    const togglePlay = () => {
        const _playing = !playing
        setPlaying(_playing)
        seekUpdate(_playing)
    }

    function seekUpdate(_playing = playingRef.current) {
        try {
            if (_playing) {
                const _seek = player.current.seek()

                setSeek(_seek)
                setPosition((_seek * 100) / order.audio.duration)
                setTimeout(seekUpdate, 250)
            }

        } catch (error) { }
    }


    const handleSpeedChange = (value) => {
        // const _rate = value / 100
        const roundedValue = Math.round(value * 10) / 10;
        if (roundedValue >= 0.6 && roundedValue <= 1.4) {
            setRate(roundedValue);
            player.current.rate(roundedValue)
        } else {
            console.log('TOO MUCH', roundedValue)
        }
    };

    const setTime = (_seek) => {
        if (loading) {
            return false
        }

        player.current.seek(_seek)
        setPosition((_seek * 100) / order.audio.duration)

        if (!playing) {
            togglePlay()
        }
    }

    const skipTime = (seconds) => {
        console.log(seconds)
        if (loading) {
            return false
        }

        const _seek = player.current.seek() + seconds
        player.current.seek(_seek)
        setPosition((_seek * 100) / order.audio.duration)
    };

    const handleSeekChange = (value) => {
        const newPosition = (value * order.audio.duration) / 100;
        setPosition(value)
        setSeek(newPosition);
        player.current.seek(newPosition)
    };

    const secondsToMinutes = (seconds) => {
        const minutes = Math.floor(parseInt(seconds) / 60);
        const remainingSeconds = parseInt(seconds) % 60;
        return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    }


    return (
        <AudioContext.Provider value={{
            loading,
            playing,
            togglePlay,
            position,
            setTime,
            seek,
            audioSettings,
            setAudioSettings,
            handleSeekChange,
            handleSpeedChange,
            rate,
            skipTime,
            secondsToMinutes,
            playerNotification
        }}>
            {uri && <ReactHowler
                onLoad={() => setLoading(false)}
                src={uri}
                playing={playing}
                rate={rate}
                volume={1}
                format={[format]}
                loop={false}
                ref={player}
            />}

            <ReactHowler
                src={'/notification.mp3'}
                loop={false}
                playing={false}
                ref={playerNotification}
            />

            {children}
        </AudioContext.Provider>
    )
}

export function useAudio() {
    const context = React.useContext(AudioContext)
    return context
}